<template lang="pug">
    section
        //- A new booking starts with the event: its registration form decides what is asked.
        .event-picker(v-if="!isUpdate && !event")
            el-form-item(:label="$t('Event')" prop="event")
                el-select.is-full-width(placeholder="" v-model="event" valueKey="id")
                    el-option(
                        v-for="event in options.event.items"
                        :disabled="isEventOptionDisabled(event)"
                        :key="event.id"
                        :label="getEventLabel(event)"
                        :value="event"
                    )
                .help {{ $t('Pick the event first. Its registration form is loaded afterwards.') }}

        el-row(v-else :gutter="40" v-loading="templateLoading")
            el-col(:lg="16" :md="16")
                //- With a registration form: its grid and order, the fixed fields bound to the booker
                registration-field-inputs(
                    v-if="templateFields.length"
                    :fields="templateFields"
                    with-fixed
                    v-model="answersModel"
                )
                    template(#salutation="{ field }")
                        el-form-item(:label="field.label" prop="booker.gender")
                            el-select.is-full-width(placeholder="" v-model="gender")
                                el-option(:label="$t('Mr.')" value="M")
                                el-option(:label="$t('Mrs.')" value="F")
                                el-option(:label="$t('Undefined')" value="X")
                    template(#first_name="{ field }")
                        el-form-item(:label="field.label" prop="booker.first_name")
                            el-input(v-model="firstName")
                    template(#last_name="{ field }")
                        el-form-item(:label="field.label" prop="booker.last_name")
                            el-input(v-model="lastName")
                    template(#email="{ field }")
                        el-form-item(:label="field.label" prop="booker.email")
                            el-input(v-model="email")
                    template(#phone="{ field }")
                        el-form-item(:label="field.label" prop="booker.phone")
                            el-input(v-model="phone")
                    template(#slot="{ field }")
                        el-form-item(:label="field.label" prop="slot.id")
                            el-select.is-full-width(placeholder="" v-model="slot" valueKey="id")
                                el-option(
                                    v-for="slot in slotOptions"
                                    :disabled="isSlotOptionDisabled(slot)"
                                    :key="slot.id"
                                    :label="getSlotLabel(slot)"
                                    :value="slot"
                                )

                //- Without one: the fixed fields in their default order
                template(v-else)
                    el-row.no-padding(:gutter="20")
                        el-col(:lg="6" :md="6")
                            el-form-item(:label="$t('Gender')" prop="booker.gender")
                                el-select.is-full-width(placeholder="" v-model="gender")
                                    el-option(:label="$t('Mr.')" value="M")
                                    el-option(:label="$t('Mrs.')" value="F")
                                    el-option(:label="$t('Undefined')" value="X")
                        el-col(:lg="9" :md="9")
                            el-form-item(:label="$t('First name')" prop="booker.first_name")
                                el-input(v-model="firstName")
                        el-col(:lg="9" :md="9")
                            el-form-item(:label="$t('Last name')" prop="booker.last_name")
                                el-input(v-model="lastName")
                    el-row.no-padding(:gutter="20")
                        el-col(:lg="12" :md="12")
                            el-form-item(:label="$t('Email')" prop="booker.email")
                                el-input(v-model="email")
                        el-col(:lg="12" :md="12")
                            el-form-item(:label="$t('Phone')" prop="booker.phone")
                                el-input(v-model="phone")
                    el-form-item(:label="$t('Slot')" prop="slot.id")
                        el-select.is-full-width(placeholder="" v-model="slot" valueKey="id")
                            el-option(
                                v-for="slot in slotOptions"
                                :disabled="isSlotOptionDisabled(slot)"
                                :key="slot.id"
                                :label="getSlotLabel(slot)"
                                :value="slot"
                            )
                    //- answers given under a form the event no longer has
                    el-form-item.registration-answers(v-if="answers.length" :label="$t('Registration form answers')")
                        .answer-list
                            .answer(v-for="answer in answers" :key="answer.label")
                                .answer-label {{ answer.label }}
                                .answer-value {{ answer.values.join(', ') }}

                el-form-item(:label="$t('Company')" prop="booker.company")
                    el-input(v-model="company")

            el-col(:lg="8" :md="8")
                el-form-item(:label="$t('Event')" prop="event")
                    el-select.is-full-width(placeholder="" v-model="event" valueKey="id")
                        el-option(
                            v-for="event in options.event.items"
                            :disabled="isEventOptionDisabled(event)"
                            :key="event.id"
                            :label="getEventLabel(event)"
                            :value="event"
                        )
                el-form-item(v-if="!isUpdate" label=" ")
                    el-switch(
                        :active-text="$t('Send email')"
                        inactive-color="#ff4949"
                        v-model="notify"
                    )
                .qr-card(v-if="isUpdate && form.qr_token")
                    .qr-card-canvas
                        canvas(ref="qrcanvas")
                    .qr-card-token {{ form.qr_token }}
                    .qr-card-footer
                        el-tag(
                            v-if="form.scanned"
                            type="success" size="small" effect="plain"
                        )
                            i.el-icon-fa-check.fas
                            |  {{ $t('Gescannt') }}
                        el-tag(v-else type="info" size="small" effect="plain")
                            i.el-icon-fa-clock.fas
                            |  {{ $t('Nicht gescannt') }}
                        span.qr-card-time(v-if="form.scanned && scannedAt") {{ scannedAt }}
</template>

<i18n>
{
  "de": {
    "Gender": "Geschlecht",
    "Salutation": "Anrede",
    "First name": "Vorname",
    "Last name": "Nachname",
    "Email": "E-Mail",
    "Phone": "Telefon",
    "Company": "Firma",
    "Mr.": "Herr",
    "Mrs.": "Frau",
    "Undefined": "Unbekannt",
    "Send email": "E-Mail versenden",
    "QR code": "QR-Code",
    "Status": "Status",
    "Gescannt": "Gescannt",
    "Nicht gescannt": "Nicht gescannt",
    "Gescannt am": "Gescannt am",
    "Registration form answers": "Angaben aus dem Anmeldeformular",
    "Pick the event first. Its registration form is loaded afterwards.": "Wähle zuerst das Event. Danach wird dessen Anmeldemaske geladen."
  }
}
</i18n>

<script>

import _ from 'lodash'
import QRCode from 'qrcode'
// import Vue from 'vue'
import { useEventManagerBookingStore } from '@/stores'

import countries from 'i18n-iso-countries'
import en from 'i18n-iso-countries/langs/en.json'
import de from 'i18n-iso-countries/langs/de.json'

countries.registerLocale(en)
countries.registerLocale(de)

import { timestamp2date } from '@/filters'

import quillConfig from '@/quill/config'

import RegistrationFieldInputs from '@/extensions/eventManager/views/Partials/RegistrationFieldInputs'

export default {
    name: 'Main',
    components: {
        RegistrationFieldInputs,
    },
    data() {
        quillConfig.modules.link.pages = this.pageOptions
        return {
            quillConfig,
        }
    },
    computed: {
        bookingStore() {
            return useEventManagerBookingStore()
        },
        gender: {
            get() {
                return _.get(this.form, 'booker.gender', '')
            },
            set(value) {
                this.bookingStore.setFormBooker({ key: 'gender', value })
            }
        },
        firstName: {
            get() {
                return _.get(this.form, 'booker.first_name', '')
            },
            set(value) {
                this.bookingStore.setFormBooker({ key: 'first_name', value })
            }
        },
        lastName: {
            get() {
                return _.get(this.form, 'booker.last_name', '')
            },
            set(value) {
                this.bookingStore.setFormBooker({ key: 'last_name', value })
            }
        },
        email: {
            get() {
                return _.get(this.form, 'booker.email', '')
            },
            set(value) {
                this.bookingStore.setFormBooker({ key: 'email', value })
            }
        },
        phone: {
            get() {
                return _.get(this.form, 'booker.phone', '')
            },
            set(value) {
                this.bookingStore.setFormBooker({ key: 'phone', value })
            }
        },
        company: {
            get() {
                return _.get(this.form, 'booker.company', '')
            },
            set(value) {
                this.bookingStore.setFormBooker({ key: 'company', value })
            }
        },
        event: {
            get() {
                return _.get(this.form, 'slot.event') || _.get(this.selections, 'event')
            },
            set(value) {
                const label = this.getEventLabel(value)
                this.bookingStore.setFormEvent(label)
                this.bookingStore.setSelectionEvent(value)

                // unset slot
                this.bookingStore.setFormSlot(undefined)
            }
        },
        slot: {
            get() {
                return _.get(this.form, 'slot')
            },
            set(value) {
                let slot = _.cloneDeep(value)

                // const events = _.get(this.options, 'event.items') || []
                // const event = events.find(event => event.id === this.event.id) || this.event
                const event = this.event
                slot.event = event

                this.bookingStore.setFormSlot(slot)
            }
        },
        notify: {
            get() {
                return _.get(this.form, 'notify') || false
            },
            set(value) {
                this.bookingStore.setFormNotify(value)
            }
        },
        slotOptions() {
            let options = []

            if (this.event) {
                const events = _.get(this.options, 'event.items') || []
                const event = events.find(event => event.id === this.event.id)

                if (_.get(event, 'slots.length')) options = event.slots
            }

            return options
        },
        isUpdate() {
            return !! _.get(this.form, 'id')
        },
        // The registration form of the booked event: from the loaded booking on update,
        // from the picked event option otherwise.
        registrationTemplateId() {
            return _.get(this.form, 'slot.event.registrationTemplate.id')
                || _.get(this.event, 'registrationTemplate.id')
                || null
        },
        templateFields() {
            return this.bookingStore.template.fields
        },
        templateLoading() {
            return this.bookingStore.template.loading
        },
        answersModel: {
            get() {
                return this.bookingStore.answers
            },
            set(answers) {
                this.bookingStore.setAnswers(answers)
            }
        },
        // Answers to the event's registration template, one entry per field: a multi-choice
        // field arrives as several rows with the same label and is shown as one line.
        answers() {
            const values = _.get(this.form, 'values') || []
            return _.map(_.groupBy(values, 'label'), (rows, label) => ({
                label,
                values: rows.map(row => row.value).filter(value => value !== null && value !== ''),
            }))
        },
        scannedAt() {
            const ts = _.get(this.form, 'scanned_at')
            return ts ? timestamp2date(ts, 'dd.MM.yyyy HH:mm') + ' Uhr' : null
        },
        scanUrl() {
            const base = window.location.origin
            const eventId = _.get(this.form, 'slot.event.id')
            const token = _.get(this.form, 'qr_token')
            if (!eventId || !token) return null
            return `${base}/event-manager/event/${eventId}/bookings?scan=${token}`
        },
        form() {
            return this.bookingStore.form
        },
        options() {
            return this.bookingStore.options
        },
        selections() {
            return this.bookingStore.selections
        },
    },
    watch: {
        scanUrl: {
            immediate: true,
            handler(url) {
                if (url) this.$nextTick(() => this.drawQrCode())
            }
        },
        registrationTemplateId: {
            immediate: true,
            handler(id) {
                this.bookingStore.loadTemplateFields(id)
            }
        }
    },
    methods: {
        drawQrCode() {
            if (!this.$refs.qrcanvas || !this.scanUrl) return
            QRCode.toCanvas(this.$refs.qrcanvas, this.scanUrl, { width: 180 })
        },
        getEventLabel(event) {
            let label = ''
            if (_.get(event, 'name')) label = event.name
            if (_.get(event, 'category.name')) label += ` - ${event.category.name}`
            return label
        },
        isEventOptionDisabled(event) {
            let disabled = false

            if (event.booking === 'none') disabled = true
            else if (!event.slots.length) disabled = true
            else if (
                event.member_limit_per_slot !== null
                && !event.slots.filter(slot => slot.bookings.length >= event.member_limit_per_slot)
            ) disabled = true

            return disabled
        },
        getSlotLabel(slot) {
            let label = ''

            if (slot.start_date === slot.end_date) {
                label = `${timestamp2date(slot.start_at)}, ${timestamp2date(slot.start_at, 'HH:mm')} - ${timestamp2date(slot.end_at, 'HH:mm')} Uhr`
            } else {
                label = `${timestamp2date(slot.start_at)}, ${timestamp2date(slot.start_at, 'HH:mm')} - ${timestamp2date(slot.end_at)}, ${timestamp2date(slot.end_at, 'HH:mm')}`
            }

            return label
        },
        isSlotOptionDisabled(slot) {
            let disabled = false

            // check in event if the current slot has capacity left

            if (this.event.booking === 'none') disabled = true
            else if (
                this.event.member_limit_per_slot !== null
                && slot.bookings.filter(booking => booking.id !== this.form.id).length >= this.event.member_limit_per_slot
            ) disabled = true

            return disabled
        }
    }
}
</script>

<style lang="sass" scoped>
.event-picker
    max-width: 520px
    .help
        line-height: 1.4
        margin-top: 6px

.qr-card
    background: #f7f9fc
    border: 1px solid #e4e9f0
    border-radius: 10px
    padding: 20px 16px 16px
    display: flex
    flex-direction: column
    align-items: center
    gap: 12px

    .qr-card-canvas
        background: #fff
        border-radius: 8px
        padding: 10px
        box-shadow: 0 1px 6px rgba(0,0,0,0.08)
        canvas
            display: block

    .qr-card-token
        font-family: monospace
        font-size: 20px
        font-weight: 700
        letter-spacing: 4px
        color: #303133
        background: #fff
        border: 1px solid #e4e7ed
        border-radius: 6px
        padding: 6px 16px

    .qr-card-footer
        display: flex
        align-items: center
        gap: 10px
        ::v-deep .el-tag i
            margin-right: 4px

    .qr-card-time
        font-size: 12px
        color: #909399
    .registration-answers
        .answer-list
            display: flex
            flex-direction: column
            gap: 6px
            line-height: 1.4
        .answer
            display: flex
            gap: 12px
            padding: 8px 12px
            border: 1px solid $--border-color-lighter
            border-radius: 4px
            background: #fafafa
        .answer-label
            flex: 0 0 40%
            font-weight: 600
            color: #606266
        .answer-value
            flex: 1
            color: #303133
            white-space: pre-wrap
</style>
